import React, {
    useLayoutEffect,
    useState,
    useRef,
    useEffect
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";

import SeatButton from "./seat-button";
import CrossIcon from "../svg/cross";
import MinusIcon from "../svg/minus";
import LoadingIndicator from "../components/loading-indicator";

const Controls = () => {
    const { zoomIn, zoomOut } = useControls();
  
    return (
      <div className="seating-plan__controls">
        <button className='btn btn--square' onClick={() => zoomIn()}>
            <span className='sr-text'>Zoom in</span>
            <CrossIcon />
        </button>
        <button className='btn btn--square' onClick={() => zoomOut()}>
            <span className='sr-text'>Zoom out</span>
            <MinusIcon />
        </button>
      </div>
    );
};

const getImageDimensions = (url, containerRef) => {
    const horizontalPadding = 80;
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            let imageScale = 0;

            if (containerRef?.current) {
                const containerRect = containerRef.current.getBoundingClientRect();

                // Subtract horizontal padding from total width to account for controls on landscape images
                const adjustedWidth = containerRect.width - horizontalPadding * 2;

                imageScale = Math.min(
                    adjustedWidth / width,
                    containerRect.height / height
                );
            }

            resolve({ width, height, image_scale: imageScale });
        };
        img.onerror = reject;
    });
};

export default function SeatingPlan(props) {
    
    const [imageDimensions, setImageDimensions] = useState(null);
    const [planImage, setPlanImage] = useState(null);
    const [seats, setSeats] = useState(null);

    const containerRef = useRef(null);
    const transformRef = useRef(null);
    const resetRef = useRef(null);
    const zoomFactor = 8;

    // Allows image used to be overridden in the Event ACF
    const { data: customPlanImageData } = useQuery({
        queryKey: ['customPlanImageData', CONSTANTS.EVENT?.event_id],
        queryFn: QUERIES.getCustomPlanImageData,
        staleTime: 86400000,
        retry: false,
        refetchOnWindowFocus: false,
        enabled: CONSTANTS.HAS_CUSTOM_PLAN_IMAGES
    });
   
    const { isPending: seatStatusisPending, 
            isError: seatStatusisError,
            data: seatStatusData, 
            error: seatStatusError } = useQuery({
        queryKey: ['seatStatusData', CONSTANTS.INSTANCE_ID],
        queryFn: QUERIES.getSeatData,
        retry: false
    });

    useEffect(() => {
        if (!props.planData) return;

        // If plan has areas create an array of seats with an area property, else return the none area seats
        let seats = [];

        if (props.planData.areas?.length) {
            seats = props.planData?.areas?.flatMap((area) =>
                (area.seats ?? []).map((seat) => ({ ...seat, area }))
            ) ?? [];
        } else {
            seats = props.planData.seats;
        }

        setSeats(seats);

    }, [props.planData]);

    useEffect(() => {
        if (customPlanImageData) {
            setPlanImage(customPlanImageData.image)
        } else {
            setPlanImage(props.planData?.background_image_url);
        }
    }, [props.planData, customPlanImageData]);

    useLayoutEffect(() => {
        if (!planImage) return;
        // Wait until the ref is attached (retry if necessary)
        const checkContainerReady = () => {
            if (containerRef?.current) {
                (async () => {
                    const image_dimensions = await getImageDimensions(
                        planImage,
                        containerRef
                    );
                    setImageDimensions(image_dimensions);
                })();
            } else {
                // If container is not yet attached, wait and retry
                requestAnimationFrame(checkContainerReady);
            }
        };
        checkContainerReady();
    }, [planImage, containerRef.current]);

    useEffect(() => {
        if (!props.planData?.areas || !transformRef.current) return;

        if (props.selectedArea?.bounds) {
            const bounds = props.selectedArea.bounds;
            const rect = containerRef.current.getBoundingClientRect();

            const zoomToBounds = (setTransform, bounds, containerWidth, containerHeight, padding = 50) => {
                const { minX, minY, maxX, maxY } = bounds;

                const boxWidth = maxX - minX;
                const boxHeight = maxY - minY;

                const centerX = minX + boxWidth / 2;
                const centerY = minY + boxHeight / 2;

                const scaleX = (containerWidth - padding * 2) / boxWidth;
                const scaleY = (containerHeight - padding * 2) / boxHeight;
                const scale = Math.min(scaleX, scaleY);

                const x = -centerX * scale + containerWidth / 2;
                const y = -centerY * scale + containerHeight / 2;

                setTransform(x, y, scale, 200, "linear");
            };

            zoomToBounds(transformRef.current, bounds, rect.width, rect.height);
        } else {
            if (!resetRef?.current) return;
            resetRef.current?.();
        }

    }, [props.selectedArea, props.planData]);

    useEffect(() => {
        if (!seatStatusData?.status || !seats) return;
        
        const selectedSeatIds = Object.entries(seatStatusData.status)
                                .filter(([key, value]) => value.status === "B") 
                                .map(([key]) => key);

        // Get an array of left and right seats for each group of seats the user has selected
        const runs = UTILS.getSeatRunsById(selectedSeatIds, seats);

        // Get the status of the 3 seats on either side of the runs of selected seats
        const statuses_around_runs = UTILS.getStatusesAroundRuns(runs, seats, seatStatusData.status);

        const runs_are_valid = UTILS.isValidSeatSelection(statuses_around_runs);

        props.setHasIsolatedSeats(!runs_are_valid);

    }, [seatStatusData, seats]);

    return (
        <div className="booking-pathway__main-content seating-plan" ref={containerRef} role="region" aria-live="polite"> 
            <h2 className="sr-text">Seating plan</h2>

            <div role="region" aria-live="assertive">
                {seatStatusisError ? 
                    <p className='error-message' role="alert">An error has occurred. {typeof seatStatusError === 'string' ? seatStatusError : seatStatusError.message}</p>
                : null}

                {props.seatingPlanError ? 
                    <p className='error-message' role="alert">{props.seatingPlanError}</p>
                : null}
            </div>
  
            { (props.planData && imageDimensions && !seatStatusisPending && !seatStatusisError && seatStatusData) ?
                (
                    <TransformWrapper      
                        initialScale={imageDimensions.image_scale}
                        minScale={imageDimensions.image_scale}
                        maxScale={imageDimensions.image_scale * zoomFactor}
                        centerOnInit={true}>
                        {({ zoomIn, zoomOut, zoomToElement, zoomTo, centerView, setTransform, resetTransform, ...rest }) => {
                            transformRef.current = setTransform;
                            resetRef.current = resetTransform;
                            return (
                            <>
                            <Controls />
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}>
                                <div className={`seating-plan__plan seating-plan__plan--${props.planData.name?.toLowerCase().replaceAll(" ", "-")}`}>
                                        <img src={planImage} 
                                            alt="Seating Plan"
                                            style={{width: imageDimensions.width, height: imageDimensions.height}} />
                                    {seats.length ?
                                        seats?.map((seat) => (
                                            <SeatButton key={seat.id} 
                                                        area={seat.area}
                                                        seat={seat} 
                                                        statusData={seatStatusData} 
                                                        groupedPrices={props.groupedPrices}
                                                        selectedArea={props.selectedArea}
                                                        selectedBands={props.selectedBands}
                                                        seatTicketIsPending={props.seatTicketIsPending}
                                                        setEnabledFilters={props.setEnabledFilters}
                                                        enabledFilters={props.enabledFilters}
                                                        addTicket={props.addTicket}
                                                        removeTicket={props.removeTicket}
                                                        selectedSeatCount={props.selectedSeatCount}
                                                        maxTickets={props.maxTickets} />
                                    ))
                                :
                                    null
                                }
                                </div>
                            </TransformComponent>
                            </>
                        )}}
                    </TransformWrapper>
                )
            : !seatStatusError ?
                (
                    <LoadingIndicator />
                )
            :null
            }
        </div>
    );
}